import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Badge,
} from "react-bootstrap";
import ReactQuill, { Quill } from "react-quill";
import ImageDropAndPaste from "quill-image-drop-and-paste";
import ImageResize from "quill-image-resize-module-react";
import "react-quill/dist/quill.snow.css";
import {
  getAllBerita,
  createBerita,
  updateBerita,
  deleteBerita,
  uploadBeritaImage,
  BASE_URL,
} from "../../services/api";
import { getStoredUser } from "../../utils/authStorage";
import "./LayananManagement.css";
import "./Dashboard.css";

// Register Image Drop module for drag and drop & paste
Quill.register("modules/imageDropAndPaste", ImageDropAndPaste);
// Register Image Resize module for resizing images
Quill.register("modules/imageResize", ImageResize);

const LayananManagement = () => {
  const [layananList, setLayananList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentLayanan, setCurrentLayanan] = useState({
    id: null,
    judul: "",
    ringkasan: "",
    isiberita: "",
    jenisberita: "",
    nourut: "",
  });
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState("");
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

  // Refs
  const quillRef = useRef(null);

  // Quill modules configuration
  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        ["link"],
        ["clean"],
      ],
      imageDropAndPaste: {
        handler: (imageDataUrl, type, imageData) => {
          // Image will be automatically inserted
        },
      },
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize", "Toolbar"],
      },
      clipboard: {
        matchVisual: true,
      },
    }),
    [],
  );

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "indent",
    "align",
    "link",
    "width",
    "height",
    "style",
  ];

  useEffect(() => {
    fetchLayanan();
  }, []);

  const fetchLayanan = async () => {
    try {
      const response = await getAllBerita();
      if (response.data.success) {
        // Filter semua kecuali berita dan welcome (anggap sisanya adalah layanan)
        const filteredLayanan = response.data.data.filter(
          (item) =>
            item.jenisberita !== "berita" && item.jenisberita !== "welcome",
        );
        // Sort by nourut (ascending)
        const sortedLayanan = filteredLayanan.sort((a, b) => {
          const numA = parseInt(a.nourut) || 999;
          const numB = parseInt(b.nourut) || 999;
          return numA - numB;
        });
        setLayananList(sortedLayanan);
      }
    } catch (error) {
      console.error("Error fetching layanan:", error);
      setAlert({
        show: true,
        type: "danger",
        message: "Gagal memuat data layanan!",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (layanan = null) => {
    if (layanan) {
      setEditMode(true);
      setCurrentLayanan(layanan);
      // Load existing thumbnail if available
      if (layanan.foto) {
        setThumbnailPreview(`${BASE_URL}/uploads/${layanan.foto}`);
      }
    } else {
      setEditMode(false);
      setCurrentLayanan({
        id: null,
        judul: "",
        ringkasan: "",
        isiberita: "",
        jenisberita: "",
        nourut: "",
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentLayanan({
      id: null,
      judul: "",
      ringkasan: "",
      isiberita: "",
      jenisberita: "",
      nourut: "",
    });
    setThumbnailPreview("");
    setThumbnailFile(null);
    setAlert({ show: false, type: "", message: "" });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentLayanan((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleQuillChange = (value) => {
    setCurrentLayanan((prev) => ({
      ...prev,
      isiberita: value,
    }));
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setAlert({
        show: true,
        type: "danger",
        message: "File harus berupa gambar!",
      });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setAlert({
        show: true,
        type: "danger",
        message: "Ukuran file maksimal 5MB!",
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setThumbnailPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload thumbnail
    setUploadingThumbnail(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("type", "thumbnail");

      const response = await uploadBeritaImage(formData);

      if (response.data.success) {
        setThumbnailFile(file);
        setCurrentLayanan((prev) => ({
          ...prev,
          foto: response.data.data.filename,
        }));

        setAlert({
          show: true,
          type: "success",
          message: "Icon/Foto layanan berhasil diupload!",
        });

        setTimeout(() => {
          setAlert({ show: false, type: "", message: "" });
        }, 2000);
      }
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
      setAlert({
        show: true,
        type: "danger",
        message: error.response?.data?.message || "Gagal mengupload icon/foto!",
      });
      setThumbnailPreview("");
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleRemoveThumbnail = () => {
    setThumbnailPreview("");
    setThumbnailFile(null);
    setCurrentLayanan((prev) => ({
      ...prev,
      foto: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setAlert({ show: false, type: "", message: "" });

    const user = getStoredUser();

    try {
      const dataToSend = {
        judul: currentLayanan.judul,
        ringkasan:
          currentLayanan.ringkasan || currentLayanan.judul.substring(0, 200),
        isiberita: currentLayanan.isiberita,
        foto: currentLayanan.foto || "",
        pengirim: user?.userid || "Admin",
        jenisberita: currentLayanan.jenisberita,
        nourut: currentLayanan.nourut || null,
      };

      let response;
      if (editMode) {
        response = await updateBerita(currentLayanan.id, dataToSend);
      } else {
        response = await createBerita(dataToSend);
      }

      if (response.data.success) {
        setAlert({
          show: true,
          type: "success",
          message: editMode
            ? "Layanan berhasil diupdate!"
            : "Layanan berhasil ditambahkan!",
        });

        setTimeout(() => {
          handleCloseModal();
          fetchLayanan();
        }, 1500);
      }
    } catch (error) {
      console.error("Error saving layanan:", error);
      setAlert({
        show: true,
        type: "danger",
        message: error.response?.data?.message || "Gagal menyimpan layanan!",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus layanan ini?")) {
      return;
    }

    try {
      const response = await deleteBerita(id);
      if (response.data.success) {
        setAlert({
          show: true,
          type: "success",
          message: "Layanan berhasil dihapus!",
        });
        fetchLayanan();

        setTimeout(() => {
          setAlert({ show: false, type: "", message: "" });
        }, 3000);
      }
    } catch (error) {
      console.error("Error deleting layanan:", error);
      setAlert({
        show: true,
        type: "danger",
        message: "Gagal menghapus layanan!",
      });
    }
  };

  const getJenisLabel = (jenis) => {
    // Capitalize first letter
    return jenis ? jenis.charAt(0).toUpperCase() + jenis.slice(1) : "-";
  };

  const getJenisIcon = (jenis) => {
    // Icon mapping untuk jenis layanan umum
    const iconMap = {
      sertifikasi: "fas fa-certificate",
      pendampingan: "fas fa-user-check",
      edukasi: "fas fa-book-open",
      konsultasi: "fas fa-headset",
    };
    return iconMap[jenis] || "fas fa-star";
  };

  const getJenisBadgeColor = (jenis) => {
    const colors = {
      sertifikasi: "primary",
      pendampingan: "success",
      edukasi: "info",
      konsultasi: "warning",
    };
    return colors[jenis] || "secondary";
  };

  return (
    <div className="layanan-management">
      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-cog"></i>
            </div>
            <div>
              <h1 className="page-header-title">Edit Layanan Kami</h1>
              <p className="page-header-subtitle">
                Kelola konten layanan yang ditampilkan di halaman utama
              </p>
            </div>
          </div>
          <Button variant="outline-light" onClick={() => handleShowModal()}>
            <i className="fas fa-plus me-2"></i>Tambah Layanan
          </Button>
        </div>
      </div>

      <Card>
        <Card.Body>
          {alert.show && (
            <Alert
              variant={alert.type}
              dismissible
              onClose={() => setAlert({ show: false })}
            >
              {alert.message}
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th style={{ width: "5%" }}>No</th>
                    <th style={{ width: "8%" }}>Urut</th>
                    <th style={{ width: "15%" }}>Jenis Layanan</th>
                    <th style={{ width: "22%" }}>Judul</th>
                    <th style={{ width: "30%" }}>Deskripsi Singkat</th>
                    <th style={{ width: "20%" }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {layananList.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                        <p className="mb-0">Belum ada layanan tersedia</p>
                        <small className="text-muted">
                          Klik tombol "Tambah Layanan" untuk menambah layanan
                          baru
                        </small>
                      </td>
                    </tr>
                  ) : (
                    layananList.map((layanan, index) => (
                      <tr key={layanan.id}>
                        <td>{index + 1}</td>
                        <td className="text-center">
                          <strong>{layanan.nourut || "-"}</strong>
                        </td>
                        <td>
                          <Badge bg={getJenisBadgeColor(layanan.jenisberita)}>
                            <i
                              className={`${getJenisIcon(layanan.jenisberita)} me-1`}
                            ></i>
                            {getJenisLabel(layanan.jenisberita)}
                          </Badge>
                        </td>
                        <td>
                          <strong>{layanan.judul}</strong>
                        </td>
                        <td>
                          {layanan.ringkasan ||
                            (layanan.isiberita
                              ? layanan.isiberita
                                  .replace(/<[^>]*>/g, "")
                                  .substring(0, 100) + "..."
                              : "-")}
                        </td>
                        <td>
                          <Button
                            variant="warning"
                            size="sm"
                            className="me-2"
                            onClick={() => handleShowModal(layanan)}
                          >
                            <i className="fas fa-edit"></i> Edit
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(layanan.id)}
                          >
                            <i className="fas fa-trash"></i> Hapus
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal for Add/Edit Layanan */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className={`fas ${editMode ? "fa-edit" : "fa-plus"} me-2`}></i>
            {editMode ? "Edit Layanan" : "Tambah Layanan"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {alert.show && (
              <Alert
                variant={alert.type}
                dismissible
                onClose={() => setAlert({ show: false })}
              >
                {alert.message}
              </Alert>
            )}

            {/* No Urut */}
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>No Urut</strong>{" "}
              </Form.Label>
              <Form.Control
                type="number"
                name="nourut"
                value={currentLayanan.nourut}
                onChange={handleInputChange}
                placeholder="Contoh: 1, 2, 3, ..."
                min="1"
              />
              <Form.Text className="text-muted">
                Urutan tampil layanan di halaman utama (kosongkan untuk urutan
                otomatis)
              </Form.Text>
            </Form.Group>

            {/* Jenis Layanan */}
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Jenis Layanan</strong>{" "}
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="jenisberita"
                value={currentLayanan.jenisberita}
                onChange={handleInputChange}
                placeholder="Contoh: sertifikasi, pendampingan, edukasi, konsultasi"
                required
              />
              <Form.Text className="text-muted">
                Masukkan jenis layanan (untuk icon default gunakan: sertifikasi,
                pendampingan, edukasi, atau konsultasi)
              </Form.Text>
            </Form.Group>

            {/* Judul */}
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Judul Layanan</strong>{" "}
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="judul"
                value={currentLayanan.judul}
                onChange={handleInputChange}
                placeholder="Contoh: Sertifikasi Halal"
                required
              />
              <Form.Text className="text-muted">
                Judul yang akan ditampilkan pada card layanan
              </Form.Text>
            </Form.Group>

            {/* Ringkasan */}
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Deskripsi Singkat</strong>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="ringkasan"
                value={currentLayanan.ringkasan}
                onChange={handleInputChange}
                placeholder="Deskripsi singkat untuk ditampilkan di card (maks. 100 karakter)"
                maxLength={100}
              />
              <Form.Text className="text-muted">
                Deskripsi singkat yang akan ditampilkan di card layanan
                (opsional, maks 100 karakter)
              </Form.Text>
            </Form.Group>

            {/* Icon/Foto Layanan */}
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Icon/Foto Layanan</strong>
              </Form.Label>
              <div className="thumbnail-upload-container">
                {thumbnailPreview ? (
                  <div className="thumbnail-preview-wrapper">
                    <img
                      src={thumbnailPreview}
                      alt="Icon layanan"
                      className="thumbnail-preview"
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      className="btn-remove-thumbnail"
                      onClick={handleRemoveThumbnail}
                      disabled={uploadingThumbnail}
                    >
                      <i className="fas fa-times"></i>
                    </Button>
                  </div>
                ) : (
                  <div className="thumbnail-upload-box">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailChange}
                      className="thumbnail-file-input"
                      id="thumbnailUpload"
                      disabled={uploadingThumbnail}
                    />
                    <label
                      htmlFor="thumbnailUpload"
                      className="thumbnail-upload-label"
                    >
                      {uploadingThumbnail ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-cloud-upload-alt fa-2x mb-2"></i>
                          <p className="mb-0">Klik atau seret gambar di sini</p>
                          <small className="text-muted">Maks. 5MB</small>
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>
              <Form.Text className="text-muted">
                Icon/foto yang akan ditampilkan di card layanan. Jika tidak
                diisi, akan menggunakan icon default sesuai jenis layanan.
              </Form.Text>
            </Form.Group>

            {/* Isi Berita / Detail */}
            <Form.Group className="mb-5">
              <Form.Label>
                <strong>Detail Layanan</strong>
              </Form.Label>
              <div className="quill-wrapper">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={currentLayanan.isiberita}
                  onChange={handleQuillChange}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Tuliskan detail lengkap layanan..."
                />
              </div>
              <Form.Text className="text-muted">
                Detail lengkap layanan (opsional, untuk pengembangan ke depan)
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer
            className="modal-footer-layanan"
            style={{ zIndex: 10000, position: "relative" }}
          >
            <Button
              variant="secondary"
              onClick={handleCloseModal}
              disabled={submitting}
              className="btn-cancel-layanan"
              style={{
                cursor: "pointer",
                pointerEvents: "auto",
                zIndex: 10001,
              }}
            >
              Batal
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={submitting}
              className="btn-submit-layanan"
              style={{
                cursor: "pointer",
                pointerEvents: "auto",
                zIndex: 10001,
              }}
            >
              {submitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Menyimpan...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  {editMode ? "Update" : "Simpan"}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default LayananManagement;
